import { apiClient } from "../axiosInstance";

class UserService {
  constructor() {
    this.baseUrl = "/users";
  }

  getUserToken() {
    return (
      localStorage.getItem("userToken") || localStorage.getItem("accessToken")
    );
  }

  // ==================== PUBLIC ENDPOINTS ====================

  /**
   * Register a new user with optional profile picture
   * @param {Object} userData - User registration data
   * @param {File} profilePicture - Optional profile picture file
   */
  async register(userData, profilePicture = null) {
    try {
      let response;

      if (profilePicture) {
        const formData = new FormData();
        Object.keys(userData).forEach((key) => {
          if (userData[key] !== undefined && userData[key] !== null) {
            formData.append(key, userData[key]);
          }
        });
        formData.append("profilePicture", profilePicture);

        response = await apiClient.post(`${this.baseUrl}/register`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await apiClient.post(`${this.baseUrl}/register`, userData);
      }

      return response.data;
    } catch (error) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw error;
    }
  }

  /**
   * Login user
   * @param {Object} credentials - { email, password }
   */
  async login(credentials) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/login`, {
        email: credentials.email,
        password: credentials.password,
      });
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw error;
    }
  }

  /**
   * Refresh access token
   * @param {string} refreshToken - Refresh token
   */
  async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/refresh-token`, {
        refreshToken,
      });
      return response.data;
    } catch (error) {
      console.error("Refresh token error:", error);
      throw error;
    }
  }

  /**
   * Verify token
   * @param {string} token - JWT token to verify
   */
  async verifyToken(token) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/verify-token`, {
        token,
      });
      return response.data;
    } catch (error) {
      console.error("Verify token error:", error);
      throw error;
    }
  }

  /**
   * Verify email with token (Public endpoint)
   * @param {string} token - Email verification token
   */
  async verifyEmailWithToken(token) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/verify-email`, {
        token,
      });
      return response;
    } catch (error) {
      console.error("Verify email with token error:", error);
      throw error;
    }
  }

  // ==================== USER PROTECTED ENDPOINTS ====================

  /**
   * Get current user profile
   */
  async getProfile() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/profile`);
      return response.data;
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  }

  /**
   * Update user profile with optional profile picture
   * @param {Object} profileData - Profile data to update
   * @param {File} profilePicture - Optional profile picture file
   */
  async updateProfile(profileData, profilePicture = null) {
    try {
      let response;

      if (profilePicture) {
        const formData = new FormData();
        Object.keys(profileData).forEach((key) => {
          if (profileData[key] !== undefined && profileData[key] !== null) {
            formData.append(key, profileData[key]);
          }
        });
        formData.append("profilePicture", profilePicture);

        response = await apiClient.patch(`${this.baseUrl}/profile`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await apiClient.patch(
          `${this.baseUrl}/profile`,
          profileData,
        );
      }

      return response.data;
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  }

  /**
   * Update user profile picture
   * @param {File} profilePicture - Profile picture file
   */
  async updateProfilePicture(profilePicture) {
    try {
      const formData = new FormData();
      formData.append("profilePicture", profilePicture);

      const response = await apiClient.post(
        `${this.baseUrl}/profile/picture`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Update profile picture error:", error);
      throw error;
    }
  }

  /**
   * Update user profile picture from base64
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} mimeType - Image MIME type
   * @param {string} alt - Optional alt text
   */
  async updateProfilePictureFromBase64(base64Data, mimeType, alt = null) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/profile/picture-base64`,
        {
          base64Data,
          mimeType,
          alt,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Update profile picture from base64 error:", error);
      throw error;
    }
  }

  /**
   * Remove user profile picture
   */
  async removeProfilePicture() {
    try {
      const response = await apiClient.delete(
        `${this.baseUrl}/profile/picture`,
      );
      return response.data;
    } catch (error) {
      console.error("Remove profile picture error:", error);
      throw error;
    }
  }

  /**
   * Get user profile picture
   */
  async getProfilePicture() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/profile/picture`);
      return response.data;
    } catch (error) {
      console.error("Get profile picture error:", error);
      throw error;
    }
  }

  /**
   * Change password
   * @param {Object} data - { currentPassword, newPassword }
   */
  async changePassword(data) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/change-password`, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  }

  /**
   * Resend verification email (Protected endpoint)
   */
  async resendVerificationEmail() {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/resend-verification`,
      );
      return response.data;
    } catch (error) {
      console.error("Resend verification email error:", error);
      throw error;
    }
  }

  /**
   * Verify email (Protected endpoint - for backward compatibility)
   */
  async verifyEmail() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/verify-email`);
      return response.data;
    } catch (error) {
      console.error("Verify email error:", error);
      throw error;
    }
  }

  /**
   * Verify phone
   */
  async verifyPhone() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/verify-phone`);
      return response.data;
    } catch (error) {
      console.error("Verify phone error:", error);
      throw error;
    }
  }

  /**
   * Toggle newsletter subscription
   * @param {boolean} isSubscribed - Subscription status
   */
  async toggleSubscription(isSubscribed) {
    try {
      const response = await apiClient.patch(
        `${this.baseUrl}/toggle-subscription`,
        {
          isSubscribed,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Toggle subscription error:", error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/logout`, {});
      return response.data;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  }

  // ==================== ADMIN ENDPOINTS (Super Admin & Admin) ====================

  /**
   * Get all users with pagination and filters (Admin only)
   * @param {Object} params - Query parameters
   */
  async getAllUsers(params = {}) {
    try {
      const response = await apiClient.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get all users error:", error);
      throw error;
    }
  }

  /**
   * Get user statistics (Admin only)
   */
  async getUserStats() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Get user stats error:", error);
      throw error;
    }
  }

  /**
   * Get recently active users (Admin only)
   * @param {number} hours - Hours to look back (default: 24)
   */
  async getRecentlyActiveUsers(hours = 24) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/recently-active`, {
        params: { hours },
      });
      return response.data;
    } catch (error) {
      console.error("Get recently active users error:", error);
      throw error;
    }
  }

  /**
   * Get inactive users (Admin only)
   * @param {number} days - Number of days of inactivity (default: 30)
   */
  async getInactiveUsers(days = 30) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/inactive`, {
        params: { days },
      });
      return response.data;
    } catch (error) {
      console.error("Get inactive users error:", error);
      throw error;
    }
  }

  /**
   * Get top customers by spending (Admin only)
   * @param {number} limit - Number of customers to return (default: 10, max: 100)
   */
  async getTopCustomers(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/top-customers`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get top customers error:", error);
      throw error;
    }
  }

  /**
   * Get users by location (Admin only)
   */
  async getUsersByLocation() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/location-stats`);
      return response.data;
    } catch (error) {
      console.error("Get users by location error:", error);
      throw error;
    }
  }

  /**
   * Get newsletter subscribers (Admin only)
   */
  async getNewsletterSubscribers() {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/newsletter-subscribers`,
      );
      return response.data;
    } catch (error) {
      console.error("Get newsletter subscribers error:", error);
      throw error;
    }
  }

  /**
   * Get users by status (Admin only)
   * @param {string} status - User status
   */
  async getUsersByStatus(status) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/status/${status}`);
      return response.data;
    } catch (error) {
      console.error("Get users by status error:", error);
      throw error;
    }
  }

  /**
   * Get user by ID (Admin only)
   * @param {number} id - User ID
   */
  async getUserById(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get user by id error:", error);
      throw error;
    }
  }

  /**
   * Get user by UUID (Admin only)
   * @param {string} uuid - User UUID
   */
  async getUserByUuid(uuid) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/uuid/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Get user by uuid error:", error);
      throw error;
    }
  }

  /**
   * Get user activity timeline (Admin only)
   * @param {number} id - User ID
   */
  async getUserActivity(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/activity`);
      return response.data;
    } catch (error) {
      console.error("Get user activity error:", error);
      throw error;
    }
  }

  /**
   * Update user status (Admin only)
   * @param {number} id - User ID
   * @param {string} status - New status
   */
  async updateUserStatus(id, status) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Update user status error:", error);
      throw error;
    }
  }

  /**
   * Force reset user password (Admin only)
   * @param {number} id - User ID
   * @param {string} newPassword - New password (min 8 characters)
   */
  async forceResetPassword(id, newPassword) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/${id}/force-reset-password`,
        {
          newPassword,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Force reset password error:", error);
      throw error;
    }
  }

  /**
   * Soft delete user (Admin only)
   * @param {number} id - User ID
   */
  async deleteUser(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Delete user error:", error);
      throw error;
    }
  }

  /**
   * Bulk delete users (Admin only)
   * @param {number[]} userIds - Array of user IDs
   */
  async bulkDeleteUsers(userIds) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/bulk-delete`, {
        userIds,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk delete users error:", error);
      throw error;
    }
  }

  /**
   * Bulk update user status (Admin only)
   * @param {number[]} userIds - Array of user IDs
   * @param {string} status - New status
   */
  async bulkUpdateStatus(userIds, status) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-status`, {
        userIds,
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update status error:", error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get status badge color based on user status
   * @param {string} status - User status
   */
  getStatusBadge(status) {
    const badges = {
      ACTIVE: {
        color:
          "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: "CheckCircle",
      },
      INACTIVE: {
        color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        icon: "XCircle",
      },
      BLOCKED: {
        color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: "AlertCircle",
      },
      PENDING: {
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: "Clock",
      },
    };
    return badges[status] || badges.INACTIVE;
  }

  /**
   * Get status label for display
   * @param {string} status - User status
   */
  getStatusLabel(status) {
    const labels = {
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      BLOCKED: "Blocked",
      PENDING: "Pending",
    };
    return labels[status] || status;
  }

  /**
   * Format date
   * @param {string} date - ISO date string
   */
  formatDate(date) {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  /**
   * Format date with time
   * @param {string} date - ISO date string
   */
  formatDateTime(date) {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

// Create and export a single instance
export const userService = new UserService();
